/** @format */

import React from "react";
import { Autocomplete, TextField } from "@mui/material";
import _ from "lodash";
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from "../../redux/commonApiCalls";

/**
 * Merchant picker for ProPilot in the admin panel.
 *
 * Uses the same cached list as the Forward Shipments filter
 * (`merchant/searchCached`). That endpoint is already RBAC-scoped server-side
 * in proship_mongo (`Util.checkForMerchantAccess` reads the user's `merchantList`
 * claim), so the options here are exactly the merchants the logged-in user is
 * allowed to see — a non-admin gets only their own merchant, a scoped admin
 * gets their assigned list, an all-access admin gets everything. No client-side
 * RBAC filtering is needed (and the ProPilot backend re-checks the same claim
 * on every /api/proship/chat call as the real security boundary).
 *
 * onChange receives the full merchant object; callers read `.id` (the OID we
 * send as `merchant_list`).
 */
export default function AdminMerchantSelect({
  value,
  onChange,
  disabled = false,
  width = 260,
}) {
  const { data: merchants = [], isFetching } =
    useGetAllActiveMerchantsIdAndNameCachedQuery();

  const options = React.useMemo(
    () => _.orderBy(merchants.filter((m) => m.is_deleted === "0"), "name"),
    [merchants]
  );

  // Slim inline control that lives in the ProPilot header where the merchant
  // panel shows "Merchant: <name>". Standard (underline) variant keeps the
  // header compact.
  return (
    <Autocomplete
      options={options}
      value={value || null}
      loading={isFetching}
      disabled={disabled}
      size="small"
      sx={{ width, maxWidth: "100%" }}
      getOptionLabel={(m) =>
        (m.companyName ? `${m.name} (${m.companyName})` : m.name) || ""
      }
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_e, m) => onChange(m)}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder="Select merchant…"
          sx={{ "& .MuiInput-input": { fontSize: 12, py: 0.25 } }}
        />
      )}
    />
  );
}
